<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import mapboxgl from 'mapbox-gl'
import type { Map as MapboxMap } from 'mapbox-gl'

interface Props {
  map: MapboxMap
  feature: GeoJSON.Feature
  isVisible?: boolean
  isLoading?: boolean
  isLoaded?: boolean
  onMarkerClick?: (feature: GeoJSON.Feature) => void
  onLoadGeometry?: (code: string) => void
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
  isLoading: false,
  isLoaded: false
})

const marker = ref<mapboxgl.Marker | null>(null)

const code = computed(() => props.feature.properties?.code)
const centerLng = computed(() => props.feature.properties?.center_lng)
const centerLat = computed(() => props.feature.properties?.center_lat)
const color = computed(() => props.feature.properties?.color || '#0059ff')

function removeMarker() {
  if (marker.value) {
    marker.value.remove()
    marker.value = null
  }
}

function createMarker() {
  if (centerLng.value == null || centerLat.value == null) return
  if (marker.value) return

  const el = buildMarkerElement()
  marker.value = new mapboxgl.Marker({ anchor: 'center', element: el })
    .setLngLat([centerLng.value, centerLat.value])
    .addTo(props.map)
}

function loadGeometry() {
  if (code.value && props.onLoadGeometry) {
    props.onLoadGeometry(code.value)
  }
}

function buildMarkerElement(): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'flex flex-col items-center'

  const box = document.createElement('div')
  box.className = 'relative w-12 h-12 rounded border-2 border-dashed flex items-center justify-center cursor-pointer'
  box.style.borderColor = color.value
  box.addEventListener('click', () => {
    props.onMarkerClick?.(props.feature)
  })

  const dot = document.createElement('div')
  dot.className = 'w-4 h-4 rounded-full border-2 border-white'
  dot.style.backgroundColor = color.value
  box.appendChild(dot)
  wrapper.appendChild(box)

  const btn = document.createElement('button')
  btn.className = 'mt-1 flex items-center gap-1 rounded bg-yellow-50 border border-yellow-300 px-2 py-0.5 text-xs font-medium text-yellow-800 hover:bg-yellow-100 disabled:opacity-60 whitespace-nowrap shadow-sm'
  btn.addEventListener('click', (e) => {
    e.stopPropagation()
    loadGeometry()
  })
  setButtonState(btn, props.isLoading)
  wrapper.appendChild(btn)

  return wrapper
}

function setButtonState(btn: HTMLButtonElement, loading: boolean) {
  if (loading) {
    btn.innerHTML = `<svg class="h-3 w-3 animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Loading...`
    btn.disabled = true
  } else {
    btn.innerHTML = `<svg class="h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg> Load`
    btn.disabled = false
  }
}

onMounted(() => {
  if (!props.isLoaded && props.isVisible) {
    createMarker()
  }
})

onBeforeUnmount(() => {
  removeMarker()
})

// Remove marker when geometry loads
watch(() => props.isLoaded, (loaded) => {
  if (loaded) {
    removeMarker()
  }
})

// Toggle marker visibility
watch(() => props.isVisible, (visible) => {
  if (!visible || props.isLoaded) {
    removeMarker()
  } else if (visible && !props.isLoaded) {
    createMarker()
  }
})

// Update button state when loading changes
watch(() => props.isLoading, (loading) => {
  const btn = marker.value?.getElement()?.querySelector('button') as HTMLButtonElement | null
  if (btn) setButtonState(btn, loading)
})
</script>

<template>
  <div style="display: none" />
</template>
